import * as Sentry from '@sentry/react';
import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import type {
    NavigationScreenIntegrationState,
    NavigationScreenSiteIntegrationsModalState,
    NavigationScreenSpaceIntegrationsModalState,
} from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Badge,
    Button,
    Dialog,
    DialogRoot,
    DialogTrigger,
    LoadingPanel,
    PageIntro,
    PageIntroActions,
    PageIntroDescription,
    PageIntroHeader,
    PageIntroHeaderIcon,
    PageIntroHeading,
    PageIntroSplitMain,
    PageIntroSplitSide,
    ProgressiveImage,
    Tooltip,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
    IntegrationInstallationStatusBadge,
    IntegrationTileIcon,
    integrationNeedsConfiguration,
    useInstallIntegration,
} from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiQuery, getSitePermissions } from '../../../state';
import { IntegrationInstallationFlow } from '../IntegrationInstallationFlow';
import { uninstallIntegrationFromSite, uninstallIntegrationFromSpace } from '../integrations.api';
import type { ContentInstallationContext } from '../types';
import { IntegrationsEmptyState } from '.';

/**
 * Intro section with install / uninstall actions for the integration screen or modal.
 */
export function IntegrationIntro(props: {
    integration: api.Integration;
    screen:
        | NavigationScreenIntegrationState
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement {
    const { integration, screen } = props;
    const { organization } = screen;
    assertIsDefined(organization);
    const { navigate } = useNavigation();
    const [previewImage] = integration.previewImages ?? [];

    const installationId = getIntegrationInstallationKey(integration.name, {
        organization,
    });

    // Status of integration installation on the organization and the current space/site
    const [
        { data: orgInstallation },
        { data: spaceInstallation },
        { data: siteInstallation },
        { data: organizationData },
        { data: siteStructure },
    ] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationInstallationById',
                {
                    installationId,
                    integrationName: integration.name,
                },
                { nullOnError: true }
            ),
            apiQuery(
                'getIntegrationSpaceInstallation',
                screen.type === 'space'
                    ? {
                          installationId,
                          spaceId: screen.space,
                          integrationName: integration.name,
                          extended: true,
                      }
                    : skipToken,
                { nullOnError: true }
            ),
            apiQuery(
                'getIntegrationSiteInstallation',
                screen.type === 'site' && installationId
                    ? {
                          installationId,
                          siteId: screen.site,
                          integrationName: integration.name,
                          extended: true,
                      }
                    : skipToken,
                { nullOnError: true }
            ),
            // Fetch organization data for permissions
            apiQuery('getOrganizationById', { organizationId: organization }),
            // Fetch site structure for site permissions
            apiQuery(
                'getSiteStructure',
                screen.type === 'site'
                    ? { organizationId: organization, siteId: screen.site }
                    : skipToken
            ),
        ],
    });

    if (siteInstallation) {
        assertIsExtendedSiteInstallation(siteInstallation);
    }

    if (spaceInstallation) {
        assertIsExtendedSpaceInstallation(spaceInstallation);
    }

    const isInstalledOnOrg = Boolean(orgInstallation);
    const contentInstallation = siteInstallation || spaceInstallation;

    const isIntegrationsModal =
        (screen.type === 'site' || screen.type === 'space') &&
        screen.sideSheet.type === 'integrations';

    const canAdminIntegrationsOnOrg = organizationData.permissions.installIntegration;
    const canAdminIntegrationsOnSpace =
        screen.type === 'space' && spaceInstallation
            ? spaceInstallation.space.permissions.installIntegration
            : null;
    const canAdminIntegrationsOnSite =
        screen.type === 'site' && siteStructure
            ? getSitePermissions({ siteStructure, organization: organizationData })
                  .installIntegration
            : null;

    const [installIntegration] = useInstallIntegration(
        integration,
        {
            organization,
        },
        isInstalledOnOrg
    );

    const configurationDialogState = useDialogTriggerState();

    return (
        <PageIntro>
            <PageIntroSplitMain>
                <PageIntroHeader>
                    <PageIntroHeaderIcon>
                        <IntegrationTileIcon
                            integration={integration}
                            className={clsx(
                                'm-nudge-icon flex items-center justify-center',
                                screen.type === 'integration' ? '[&>*]:size-6' : '[&>*]:size-5'
                            )}
                            size={screen.type === 'integration' ? 40 : 32}
                        />
                    </PageIntroHeaderIcon>
                    <PageIntroHeading size="medium">{integration.title}</PageIntroHeading>
                    {integration.visibility === api.IntegrationVisibility.Private ? (
                        <PrivateIntegrationBadge />
                    ) : null}
                    {isIntegrationsModal && contentInstallation ? (
                        <IntegrationInstallationStatusBadge
                            isActive={
                                contentInstallation?.status ===
                                api.IntegrationInstallationStatus.Active
                            }
                        />
                    ) : null}
                </PageIntroHeader>
                <PageIntroDescription>{integration.description}</PageIntroDescription>
                <PageIntroActions>
                    <>
                        {/* "Edit configuration" only possible in the integrations modal 
                        for users with admin permissions on the space/site. */}
                        {orgInstallation &&
                        isIntegrationsModal &&
                        contentInstallation &&
                        integrationNeedsConfiguration(integration) &&
                        (canAdminIntegrationsOnSite || canAdminIntegrationsOnSpace) ? (
                            <DialogRoot {...configurationDialogState.rootProps}>
                                <DialogTrigger>
                                    <Button
                                        kind="secondary"
                                        label="Edit configuration"
                                        leadingIcon={Icon.Settings}
                                    />
                                </DialogTrigger>
                                <Dialog size={{ width: 912, height: 640 }}>
                                    <ConfigurationFlow
                                        integration={integration}
                                        installation={orgInstallation}
                                        context={
                                            'site' in contentInstallation
                                                ? {
                                                      type: 'site',
                                                      installation: contentInstallation,
                                                  }
                                                : {
                                                      type: 'space',
                                                      installation: contentInstallation,
                                                  }
                                        }
                                        onClose={configurationDialogState.close}
                                    />
                                </Dialog>
                            </DialogRoot>
                        ) : null}

                        {/* Make sure that users see actions based on permissions or an appropriate message to indcate lack of permissions */}
                        {(screen.type === 'integration' && canAdminIntegrationsOnOrg) ||
                        // On the space screen modal, only space or org admin users see the button
                        (screen.type === 'space' &&
                            (canAdminIntegrationsOnOrg ||
                                (canAdminIntegrationsOnSpace && isInstalledOnOrg))) ||
                        // On the site screen modal, only site or org admin users see the button
                        (screen.type === 'site' &&
                            (canAdminIntegrationsOnOrg ||
                                (canAdminIntegrationsOnSite && isInstalledOnOrg))) ? (
                            <Button
                                leadingIcon={Icon.IntegrationsInstall}
                                label={(() => {
                                    switch (screen.type) {
                                        case 'integration':
                                            if (
                                                integration.target ===
                                                api.IntegrationTarget.Organization
                                            ) {
                                                return isInstalledOnOrg ? 'Configure' : 'Install';
                                            }

                                            return isInstalledOnOrg
                                                ? `Install on a ${integration.target === api.IntegrationTarget.All ? 'docs site or space' : integration.target === api.IntegrationTarget.Site ? 'docs site' : 'space'}`
                                                : 'Install';
                                        case 'space':
                                            return spaceInstallation
                                                ? 'Uninstall from this space'
                                                : 'Install on this space';
                                        case 'site':
                                            return siteInstallation
                                                ? 'Uninstall from this site'
                                                : 'Install on this site';
                                        default:
                                            return 'Install';
                                    }
                                })()}
                                kind={
                                    isInstalledOnOrg || spaceInstallation || siteInstallation
                                        ? 'secondary'
                                        : 'primary'
                                }
                                onPress={async () => {
                                    await toast.catch(async () => {
                                        // handle uninstall from site
                                        if (screen.type === 'site' && siteInstallation) {
                                            return uninstallIntegrationFromSite({
                                                integrationName: integration.name,
                                                installationId,
                                                siteId: siteInstallation.site.id,
                                                organizationId: organization,
                                            });
                                        }
                                        // handle uninstall from space
                                        if (screen.type === 'space' && spaceInstallation) {
                                            return uninstallIntegrationFromSpace({
                                                integrationName: integration.name,
                                                installationId,
                                                spaceId: spaceInstallation.space.id,
                                                organizationId: organization,
                                            });
                                        }

                                        // handle install in site
                                        if (screen.type === 'site' && !siteInstallation) {
                                            await installIntegration({
                                                site: screen.site,
                                                suppressToast: true,
                                            });

                                            if (integrationNeedsConfiguration(integration)) {
                                                configurationDialogState.open();
                                            }

                                            return;
                                        }

                                        // handle install in space
                                        if (screen.type === 'space' && !spaceInstallation) {
                                            await installIntegration({
                                                space: screen.space,
                                                suppressToast: true,
                                            });

                                            if (integrationNeedsConfiguration(integration)) {
                                                configurationDialogState.open();
                                            }

                                            return;
                                        }

                                        // handle org installation
                                        if (screen.type === 'integration') {
                                            if (!isInstalledOnOrg) {
                                                await installIntegration({
                                                    suppressToast: true,
                                                });
                                            }

                                            navigate({
                                                current: { installation: { step: 'flow' } },
                                            });
                                        }
                                    }, 'Error while uninstalling integration');
                                }}
                            />
                        ) : (
                            <Alert
                                size="small"
                                kind="info"
                                details="Contact an administrator to install or configure this integration."
                            />
                        )}
                    </>
                </PageIntroActions>
            </PageIntroSplitMain>

            {previewImage && (
                <PageIntroSplitSide>
                    <ProgressiveImage
                        className={clsx(
                            'h-auto w-full object-cover',
                            screen.type !== 'integration' && 'max-h-[11.5rem] max-w-[23rem]'
                        )}
                        style={{ aspectRatio: 368 / 184 }}
                        src={previewImage}
                        width={1024}
                    />
                </PageIntroSplitSide>
            )}
        </PageIntro>
    );
}

/**
 * Flow for configuration the integration
 */
function ConfigurationFlow(props: {
    integration: api.Integration;
    installation: api.IntegrationInstallation;
    context: ContentInstallationContext;
    onClose: () => void;
}): React.ReactElement {
    const { integration, installation, context, onClose } = props;

    return (
        <Sentry.ErrorBoundary fallback={<IntegrationsEmptyState kind="error" />}>
            <React.Suspense fallback={<LoadingPanel />}>
                <IntegrationInstallationFlow
                    skipAside
                    organizationId={installation.target.organization}
                    integration={integration}
                    installation={installation}
                    onComplete={onClose}
                    stepState={{
                        step: 'configure_content_installation',
                        context,
                    }}
                />
            </React.Suspense>
        </Sentry.ErrorBoundary>
    );
}

function PrivateIntegrationBadge() {
    return (
        <Tooltip label="This integration is private and only visible to your organization">
            <Badge
                kind="secondary"
                label="Private"
                aria-label="Private integration"
                icon={Icon.Lock}
                className="m-nudge-icon"
            />
        </Tooltip>
    );
}
